import { Box, Button, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { NotificationEvent } from "@dashboard-v3/api";
import syncErrorReason from "utils/syncErrorReason";

const ACCOUNTS_PATH = "/integrations?tab=ACCOUNTS";

const datasetPath = (datasetId: string) => `/datasets/${datasetId}/edit`;

/**
 * Detalle de la notificacion `dataset-sync-failure` (MXH-5844).
 *
 * Hasta ahora un sync roto -tipicamente una cuenta de cloud storage que necesita
 * reautorizarse- solo se veia entrando al dataset, asi que podia quedar fallando
 * semanas sin que nadie se enterara.
 *
 * Desde MXH-5913 el evento trae la causa clasificada, y esta pantalla muestra el
 * mismo motivo y ofrece la misma accion que el detalle del run en Datasets. Lo
 * que ve el cliente sigue siendo el texto traducido, nunca el codigo.
 *
 * Antes no la traia y el texto no podia afirmar una causa, asi que mandaba
 * siempre a revisar la autorizacion de la cuenta. Con un error de scope
 * `STORAGE_ITEM` eso era directamente equivocado: la cuenta esta sana y el
 * problema es la carpeta configurada.
 */
export default function DatasetSyncFailureDetails({
  notification,
}: {
  notification: NotificationEvent;
}) {
  // `datasets` es el namespace del motivo: la key es la misma que usa el detalle
  // del run, para que las dos pantallas no puedan decir cosas distintas.
  const { t } = useTranslation(["notifications", "storage", "datasets"]);
  const {
    datasetId,
    datasetName,
    cloudStorageType,
    cloudStorageUser,
    errorCode,
    errorScope,
  } = notification.data;

  // El nombre se resuelve al notificar y puede faltar. El id no es lindo pero
  // identifica el dataset, que es lo que hace falta para ir a mirarlo.
  const dataset = datasetName || datasetId;

  return (
    <Box
      style={{ display: "flex", flexDirection: "column", gap: "10px" }}
      data-testid="notification-dataset-sync-failure"
    >
      <Typography component="span">
        {dataset
          ? t("notifications:datasetSyncFailure.title", {
              defaultValue: "The dataset {{dataset}} could not be synchronized",
              dataset,
              interpolation: { escapeValue: false },
            })
          : t("notifications:datasetSyncFailure.titleGeneric", {
              defaultValue: "A dataset could not be synchronized",
            })}
      </Typography>

      <Typography variant="body2" color="textSecondary">
        {errorCode
          ? syncErrorReason(t, errorCode)
          : t("notifications:datasetSyncFailure.description", {
              defaultValue:
                "The scheduled synchronization failed and the dataset is no longer being updated.",
            })}
      </Typography>

      {cloudStorageUser && <FailedAccount notification={notification} />}

      {/* Solo cuando el problema es la cuenta. Sin scope -las notificaciones
          anteriores a MXH-5913- se muestra igual, que es como se veian. */}
      {(!errorScope || errorScope === "STORAGE_ACCOUNT") && (
        <ReauthorizeAlert
          cloudStorageType={cloudStorageType}
          cloudStorageUser={cloudStorageUser}
        />
      )}

      {/* Sin `datasetId` no hay a donde ir: el link se omite en vez de apuntar a
          una ruta rota. */}
      {datasetId && (
        <Box>
          <Button
            component={RouterLink}
            to={datasetPath(datasetId)}
            color="primary"
            size="small"
            data-testid="notification-dataset-sync-failure-link"
          >
            {t("notifications:datasetSyncFailure.goToDataset", {
              defaultValue: "Go to dataset",
            })}
          </Button>
        </Box>
      )}
    </Box>
  );
}

/** Contra que cuenta corria el sync que fallo. */
function FailedAccount({ notification }: { notification: NotificationEvent }) {
  const { t } = useTranslation(["notifications", "storage"]);
  const { cloudStorageType, cloudStorageUser } = notification.data;

  return (
    <Typography variant="caption" color="textSecondary" component="div">
      {t("notifications:datasetSyncFailure.account", {
        defaultValue: "Account: {{account}} ({{provider}})",
        account: cloudStorageUser,
        provider: providerName(t, cloudStorageType),
        interpolation: { escapeValue: false },
      })}
    </Typography>
  );
}

/**
 * Lo unico que el usuario puede resolver solo. Se muestra siempre y no solo
 * cuando sabemos que la credencial vencio, porque el evento no trae la causa y
 * la reautorizacion es igual el primer lugar donde mirar.
 *
 * El link va al listado de cuentas y no a la autorizacion directa porque el
 * flujo de auth cambia segun el proveedor y ya vive resuelto en esa pagina
 * (mismo criterio que `Datasets/Sync/SyncRunError`).
 */
function ReauthorizeAlert({
  cloudStorageType,
  cloudStorageUser,
}: {
  cloudStorageType?: string;
  cloudStorageUser?: string;
}) {
  const { t } = useTranslation(["notifications", "storage"]);

  // Los `defaultValue` no son decorativos: si la key falta en locize, i18next
  // devuelve la key cruda y el usuario leeria "datasetSyncFailure.reauthorize".
  const message =
    cloudStorageUser && cloudStorageType
      ? t("notifications:datasetSyncFailure.reauthorizeAccount", {
          defaultValue:
            "Check that the {{provider}} account {{account}} is still authorized",
          account: cloudStorageUser,
          provider: providerName(t, cloudStorageType),
          interpolation: { escapeValue: false },
        })
      : t("notifications:datasetSyncFailure.reauthorizeGeneric", {
          defaultValue:
            "Check that the cloud storage account is still authorized",
        });

  return (
    <Alert
      style={{ alignItems: "center" }}
      severity="warning"
      data-testid="notification-dataset-sync-failure-reauthorize"
      action={
        <Button
          component={RouterLink}
          to={ACCOUNTS_PATH}
          color="inherit"
          size="small"
          data-testid="notification-dataset-sync-failure-reauthorize-link"
        >
          {t("notifications:datasetSyncFailure.goToAccounts", {
            defaultValue: "Go to accounts",
          })}
        </Button>
      }
    >
      {message}
    </Alert>
  );
}

/** Si falta la traduccion del proveedor mostramos el crudo ("BOX"), que se lee igual. */
function providerName(
  t: (key: string, opts?: object) => string,
  provider?: string
) {
  if (!provider) return "";
  return t(`storage:accountItem.provider.${provider}`, {
    defaultValue: provider,
  });
}
